import React, { useMemo, useState } from 'react'
import Button from '../../../Components/Button'
import { formatPrice } from '../utils/formatters'
import useModalAccessibility from '../hooks/useModalAccessibility'

// Bottom-sheet picker shown when a guest adds an item that has optional add-ons.
// Each add-on is an independent checkbox; the confirm button shows the running
// unit price (base item + ticked add-ons) and adds one line to the basket.
export default function AddOnPickerModal({ item, onClose, onConfirm }) {
  const addOns = item.add_ons || []
  const [selected, setSelected] = useState(() => new Set())
  const { dialogRef, isVisible, handleClose } = useModalAccessibility({
    onClose,
    initialFocusSelector: 'input[type="checkbox"]'
  })

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const unitPrice = useMemo(() => {
    const addOnTotal = addOns.reduce(
      (sum, a) => (selected.has(a.id) ? sum + a.price_pence : sum),
      0
    )
    return item.price_pence + addOnTotal
  }, [addOns, selected, item.price_pence])

  const handleConfirm = () => {
    onConfirm(Array.from(selected))
    handleClose()
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      role="dialog"
      aria-modal="true"
      aria-label={`Add extras to ${item.name}`}
      ref={dialogRef}
    >
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Sheet */}
      <div className={`relative bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[80vh] flex flex-col transition-transform duration-300 ease-out ${
        isVisible ? 'translate-y-0' : 'translate-y-full'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="min-w-0">
            <h2 className="text-xl font-bold text-gray-900 truncate">{item.name}</h2>
            <p className="text-sm text-gray-500">Add extras (optional)</p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none focus:outline-none focus:ring-2 focus:ring-sky-500 rounded"
            aria-label="Close"
            data-modal-close
          >
            ✕
          </button>
        </div>

        {/* Add-ons */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <ul className="space-y-1" aria-label="Available add-ons">
            {addOns.map(addOn => (
              <li key={addOn.id}>
                <label className="flex items-center justify-between gap-4 py-3 cursor-pointer">
                  <span className="flex items-center gap-3 min-w-0">
                    <input
                      type="checkbox"
                      checked={selected.has(addOn.id)}
                      onChange={() => toggle(addOn.id)}
                      className="h-5 w-5 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                    />
                    <span className="text-gray-900 truncate">{addOn.name}</span>
                  </span>
                  <span className="text-gray-600 flex-shrink-0">+{formatPrice(addOn.price_pence)}</span>
                </label>
              </li>
            ))}
          </ul>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50">
          <Button
            onClick={handleConfirm}
            variant="primary"
            theme="dark"
            size="lg"
            fullWidth
            ariaLabel={`Add ${item.name} to basket for ${formatPrice(unitPrice)}`}
          >
            Add to basket · {formatPrice(unitPrice)}
          </Button>
        </div>
      </div>
    </div>
  )
}
